/**
 * 현장/메모 통합검색 도움 레이어 — $j.F.Order.SearchHelp
 *
 * 관리자 접수(/adm/orders)와 협력사 접수(/p/orders) 공용.
 *
 * 이 칸(#stx)은 이름이 '현장,메모' 지만 실제로는 오더 색인(g5_order_search) 전체를 뒤진다.
 * 발주자·수주자 이름과 연락처, 차량번호, 접수번호까지 걸리는데 화면엔 그 사실이 없어서
 * "왜 이게 나오지 / 왜 이건 안 나오지" 가 반복됐다. 칸을 누르면 무엇이 검색되는지 펼쳐 보여준다.
 *
 * 레이어는 **안내 전용**이다. 입력은 원래 칸(#stx)에서 그대로 받는다 —
 * 입력칸이 둘이면 "어디에 친 값이 진짜인지" 가 다시 헷갈리기 때문이다.
 * 엔터 검색은 #stx 의 data-enter-search(SearchOpt) 가 처리하고, 여기선 레이어만 닫는다.
 *
 * ⚠️ 아래 항목 목록은 App\Services\Search\SearchEngineService::ENTITIES['order'] 의 색인 컬럼과
 *    1:1 이다. 색인 대상을 늘리면 여기도 같이 고쳐야 한다.
 */
export default class SearchHelp {

    /**
     * 검색되는 항목 — 색인 컬럼을 사용자 언어로 묶은 것.
     * admOnly 는 관리자 화면에서만 노출한다 (협력사는 관리메모를 볼 수 없다).
     */
    static FIELDS = [
        { key: '현장', desc: '주소 · 현장명 · 현장담당자 이름/연락처' },
        { key: '발주자', desc: '이름 · 연락처' },
        { key: '수주자', desc: '기사 이름/직통번호 · 사장 이름' },
        { key: '장비', desc: '차량번호 (예: 66너3888)' },
        { key: '메모', desc: '작업메모 (발주자 작성)' },
        { key: '메모', desc: '관리메모 (CS 작성)', admOnly: true },
        { key: '번호', desc: '수임·접수번호 · 보상담당자 이름/연락처' },
        { key: '환원', desc: '환원보류 · 환원완료' },
    ];

    /**
     * 검색 규칙 — SearchService::matchSql() 의 실제 동작이다.
     * 여기 적힌 게 곧 "왜 안 찾히는지" 의 답이라 문구를 임의로 줄이지 말 것.
     */
    static TIPS = [
        '두 글자 이상 입력해야 검색됩니다. (한 글자는 결과가 0건으로 나옵니다)',
        '전화번호·사업자번호는 <b>전체를 입력</b>하세요. 뒷자리만으로는 찾히지 않습니다.',
        '띄어쓰기로 나눠 쓰면 <b>모두 포함</b>된 오더만 나옵니다. (예: <code>김포 아파트</code>)',
        '업종·진행상태·기간은 여기서 검색되지 않습니다. 위쪽 버튼과 날짜칸으로 걸러주세요.',
    ];

    /**
     * 도움 레이어를 #stx 인풋에 붙인다.
     * @param {object} opt { isAdmin:boolean, onChange:function } onChange 는 검색어가 바뀔 때 호출
     */
    static init(opt = {}) {
        const $input = $('#stx');
        if (!$input.length || this._inited) return;
        this._inited = true;
        this._opt = opt;

        this.build(opt);

        // 인풋을 누르면 안내를 편다. 포커스는 #stx 에 그대로 두고 거기서 타이핑한다.
        $input.on('click focus', (e) => { e.stopPropagation(); this.open(); });

        // 값이 바뀌면 인풋 배경색("기본값과 다름") 갱신 — #stx 에는 data-refresh-keys 가 없다
        $input.on('input', () => { if (typeof this._opt.onChange === 'function') this._opt.onChange(); });

        // 엔터 검색은 SearchOpt 가 처리한다. 여기선 결과를 가리지 않게 레이어만 닫는다.
        $input.on('keydown', (e) => { if (e.key === 'Enter') this.close(); });

        // 바깥 클릭 / ESC 로 닫기. 값은 이미 #stx 에 반영돼 있어 잃을 게 없다.
        // 두 번 불려도 한 벌만 남게 먼저 걷어낸다 (접수목록과 협력사 목록이 각자 부른다)
        $(document).off('.searchHelp');
        $(window).off('.searchHelp');
        $(document).on('click.searchHelp', (e) => {
            if (!this._$layer) return;
            if ($(e.target).closest('#stx-help, #stx').length) return;
            this.close();
        });
        $(document).on('keydown.searchHelp', (e) => { if (e.key === 'Escape') this.close(); });
        $(window).on('resize.searchHelp scroll.searchHelp', () => { if (this._$layer && this._$layer.is(':visible')) this.place(); });
    }

    /** 레이어 DOM 생성. <body> 직속에 붙인다 — 필터 영역 td 가 overflow:hidden 이라 안에 두면 잘린다. */
    static build(opt) {
        const rows = this.FIELDS
            .filter((f) => !f.admOnly || opt.isAdmin)
            .map((f) => `<li><span class="k">${f.key}</span><span class="d">${f.desc}</span></li>`)
            .join('');
        const tips = this.TIPS.map((t) => `<li>${t}</li>`).join('');

        this._$layer = $(`
            <div id="stx-help" class="stx-help" style="display:none">
                <div class="stx-help-head">
                    <span class="ttl">통합검색</span>
                    <span class="sub">현장·사람·장비·메모를 한번에 찾습니다</span>
                    <button type="button" class="stx-help-close" title="닫기">&times;</button>
                </div>
                <div class="stx-help-body">
                    <div class="stx-help-sec">
                        <div class="stx-help-sec-ttl">이런 것들이 함께 검색됩니다</div>
                        <ul class="stx-help-fields">${rows}</ul>
                    </div>
                    <div class="stx-help-sec">
                        <div class="stx-help-sec-ttl">검색 요령</div>
                        <ul class="stx-help-tips">${tips}</ul>
                    </div>
                </div>
            </div>
        `).appendTo('body');

        // 레이어 안을 눌러도 닫히지 않게 (바깥클릭 닫기와 충돌 방지)
        this._$layer.on('click', (e) => e.stopPropagation());
        this._$layer.find('.stx-help-close').on('click', () => this.close());
    }

    /** 안내를 편다. 포커스는 건드리지 않는다 — 입력은 #stx 에서 계속 받는다. */
    static open() {
        if (!this._$layer) return;
        this._$layer.show();
        this.place();
    }

    static close() {
        if (this._$layer) this._$layer.hide();
    }

    /** #stx 바로 아래에 위치시킨다. body 직속이라 문서좌표로 계산한다. */
    static place() {
        const el = document.getElementById('stx');
        if (!el || !this._$layer) return;
        const r = el.getBoundingClientRect();
        const w = this._$layer.outerWidth();
        // 화면 오른쪽으로 넘치면 안쪽으로 당긴다
        let left = r.left + window.scrollX;
        const overflow = (left + w) - (window.scrollX + document.documentElement.clientWidth - 8);
        if (overflow > 0) left -= overflow;
        this._$layer.css({ top: r.bottom + window.scrollY + 6, left: Math.max(8, left) });
    }
}
